import axios from 'axios';
import { jwtDecode } from 'jwt-decode';


// Create a base axios instance for refreshing tokens
const baseAxios = axios.create({  
  baseURL: import.meta.env.VITE_API_URL || '/api',
  headers: {
    'Content-Type': 'application/json',
  },
});

// Create the main axios instance
const instance = axios.create({
  baseURL: import.meta.env.VITE_API_URL || '/api',
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true,
});

// Store the Promise for refreshing tokensq
let isRefreshing = false;
let refreshSubscribers = [];
let refreshTimeout = null;

// Process the requests in the queue
const processQueue = (error, token = null) => {
  refreshSubscribers.forEach(cb => cb(error, token));
  refreshSubscribers = [];
};

// Add to the subscriber queue
const addSubscriber = (callback) => {
  refreshSubscribers.push(callback);
};

// Check if the token is expiring soon
const isTokenExpiringSoon = (token) => {
  if (!token) return false;
  try {
    const decoded = jwtDecode(token);
    const expirationTime = decoded.exp * 1000; // Convert to milliseconds
    const currentTime = Date.now();
    // If the token will expire in 15 minutes, return true
    return expirationTime - currentTime < 15 * 60 * 1000;
  } catch (error) {
    console.error('Error decoding token:', error);
    return false;
  }
};

// Set up the auto refresh timer
const setupRefreshTimer = (token) => {
  if (!token) return;
  
  try {
    const decoded = jwtDecode(token);
    const expirationTime = decoded.exp * 1000;
    const currentTime = Date.now();
    const timeUntilRefresh = expirationTime - currentTime - (15 * 60 * 1000); // Refresh 15 minutes ahead of time
    
    // Clear the existing timer
    if (refreshTimeout) {
      clearTimeout(refreshTimeout);
    }
    
    if (timeUntilRefresh > 0) {
      refreshTimeout = setTimeout(async () => {
        const currentToken = localStorage.getItem('token');
        if (currentToken && isTokenExpiringSoon(currentToken)) {
          try {
            const newToken = await refreshToken();
            if (newToken) {
              console.log('Token automatically refreshed');
            }
          } catch (error) {
            console.error('Auto refresh failed:', error);
          }
        }
      }, timeUntilRefresh);
    }
  } catch (error) {
    console.error('Error setting up refresh timer:', error);
  }
};

// Refresh token
const refreshToken = async () => {
  const oldToken = localStorage.getItem('token');
  try {
    const { data } = await baseAxios.post('/auth/refresh-token', {}, {
      headers: {
        'Authorization': `Bearer ${oldToken}`,
        'Content-Type': 'application/json',
      }
    });
    localStorage.setItem('token', data.access_token);
    setupRefreshTimer(data.access_token);
    return data.access_token;
  } catch (error) {
    console.error('Token refresh failed:', error);
    return null;
  }
};

// Request interceptor
instance.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
      // TODO: DELETE IN PRODUCTION leaking token//
      // console.log('Request with token:', config.url, config.headers.Authorization);
      // Check if the token is expiring soon
      if (isTokenExpiringSoon(token) && !config.url.includes('/auth/refresh-token')) {
        console.log('Token expiring soon, refreshing...');
        refreshToken().catch(console.error);
      }
    } else {
      console.log('No token found for request:', config.url);
    }
    return config;
  },
  (error) => {
    console.error('Request interceptor error:', error);
    return Promise.reject(error);
  }
);

// Response interceptor
instance.interceptors.response.use(
  (response) => response,
  async (error) => {
    console.error('Response error:', error.response?.status, error.config?.url);
    const originalRequest = error.config;

    // If it's a 401 error and not a refresh token request
    if (error.response?.status === 401 && !originalRequest._retry && !originalRequest.url.includes('/auth/refresh-token')) {
      console.log('Attempting to refresh token...');
      if (isRefreshing) {
        // If the token is being refreshed, add the request to the queue
        return new Promise((resolve, reject) => {
          addSubscriber((error, token) => {
            if (error) {
              reject(error);
            } else {
              originalRequest.headers.Authorization = `Bearer ${token}`;
              resolve(instance(originalRequest));
            }
          });
        });
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        const newToken = await refreshToken();
        if (newToken) {
          // Refresh successful, retry the original request
          originalRequest.headers.Authorization = `Bearer ${newToken}`;
          processQueue(null, newToken);
          return instance(originalRequest);
        } else {
          // Refresh failed, clear the token and redirect to the login page
          processQueue(new Error('Failed to refresh token'));
          localStorage.removeItem('token');
          if (!window.location.pathname.includes('/login')) {
            window.location.href = '/login';
          }
          return Promise.reject(error);
        }
      } finally {
        isRefreshing = false;
      }
    }

    return Promise.reject(error);
  }
);

// Set up the refresh timer when initializing
const initToken = localStorage.getItem('token');
if (initToken) {
  setupRefreshTimer(initToken);
}

export default instance;
